import { memo } from 'react'
import { Phone, UserMinus } from 'lucide-react'

import { cn } from '@/lib/utils'

import { SHOW_WORKER_RESPONSE_STATUS, type RemovalRule } from '../schedulingRules'
import type { Job, JobWorker, Worker, WorkerResponse } from '../types'
import { formatDistance, hasValidCoords, haversineKm } from '../utils/distance'
import { formatCompactAgo } from '../utils/time'
import { WorkerAvatar, WorkerResponseChip } from './status-meta'

/** "240 m · seen 1h ago", "1.2 km", "Location unavailable". */
export function describeWorkerLocation(worker: Worker | null, job: Pick<Job, 'lat' | 'lng'>, now: number): string {
  if (!worker || !hasValidCoords(worker)) return 'Location unavailable'
  const distance = hasValidCoords(job) ? formatDistance(haversineKm(worker, job)) : null
  const seen = worker.locationSource && worker.locationSource !== 'live' ? formatCompactAgo(worker.lastSeenAt, now) : null
  return [distance, seen ? `seen ${seen}` : null].filter(Boolean).join(' · ') || 'Location shared'
}

export interface AssignedWorkerRowProps {
  job: Job
  jobWorker: JobWorker
  worker: Worker | null
  response: WorkerResponse
  removal: RemovalRule
  /** card: name + "Remove" text button. popup: name + location line + Call / Remove icon buttons. */
  variant: 'card' | 'popup'
  now: number
  isHovered: boolean
  /** Changes when the row should flash after a live update (0 = none). */
  flashToken?: number
  onHover: (workerId: string | null) => void
  onRemove: (jobId: string, workerId: string) => void
}

/** One assigned worker. Remove is disabled (with the reason) when the rules block it. */
export const AssignedWorkerRow = memo(function AssignedWorkerRow({
  job,
  jobWorker,
  worker,
  response,
  removal,
  variant,
  now,
  isHovered,
  flashToken = 0,
  onHover,
  onRemove,
}: AssignedWorkerRowProps) {
  const name = worker?.name ?? 'Unknown worker'
  const workerId = jobWorker.workerId
  const removeLabel = removal.allowed ? `Remove ${name} from ${job.bookingNo}` : `${removal.blockedReason}: ${name}`
  const handleRemove = () => onRemove(job.id, workerId)

  return (
    <li
      data-worker-row={`${job.id}|${workerId}`}
      onMouseEnter={() => onHover(workerId)}
      onMouseLeave={() => onHover(null)}
      className={cn(
        'relative flex items-center gap-1.5 rounded-md px-1 py-1 transition-colors',
        isHovered ? 'bg-[#eef6f1]' : 'bg-transparent',
      )}
    >
      {flashToken ? <span key={flashToken} className="rt-flash pointer-events-none absolute inset-0 rounded-md" aria-hidden="true" /> : null}
      <WorkerAvatar name={name} avatarUrl={worker?.avatarUrl ?? null} className="h-6 w-6" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12px] font-semibold leading-tight text-[#20352b]">{name}</p>
        {variant === 'popup' ? (
          <p className="truncate text-[10px] leading-tight text-[#718078]">{describeWorkerLocation(worker, job, now)}</p>
        ) : null}
        {SHOW_WORKER_RESPONSE_STATUS ? <WorkerResponseChip jobWorker={jobWorker} response={response} className="mt-0.5 px-1.5 text-[10px]" /> : null}
      </div>

      {variant === 'popup' && worker?.phone ? (
        <a
          href={`tel:${worker.phone.replace(/\s+/g, '')}`}
          aria-label={`Call ${name}`}
          title={`Call ${name}`}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#355445] hover:bg-[#e8f4ee] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#169b62]"
        >
          <Phone className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      ) : null}

      {variant === 'popup' ? (
        <button
          type="button"
          onClick={handleRemove}
          disabled={!removal.allowed}
          aria-label={removeLabel}
          title={removal.blockedReason ?? `Remove ${name}`}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#718078] hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <UserMinus className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : (
        <button
          type="button"
          onClick={handleRemove}
          disabled={!removal.allowed}
          aria-label={removeLabel}
          title={removal.blockedReason ?? undefined}
          className="inline-flex h-6 shrink-0 items-center gap-1 rounded-md border border-[#e3ebe6] bg-white px-1.5 text-[11px] font-medium text-[#4e6157] hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[#e3ebe6] disabled:hover:bg-white disabled:hover:text-[#4e6157]"
        >
          <UserMinus className="h-3 w-3" aria-hidden="true" />
          Remove
        </button>
      )}
    </li>
  )
})
